import * as KatalMetrics from '@amzn/katal-metrics';
import initialMetricsPublisher from '../metricsPublisher';
import { MetricNames, DimensionNames, LoginMethodValues } from './constants';

/**
 * Extracted dimensions from URL parameters and environment
 */
interface MetricsDimensions {
    [DimensionNames.SITE_CODE]: string | null;
    [DimensionNames.LOGIN_METHOD]: LoginMethodValues | null;
    [DimensionNames.STAGE]: string;
}

/**
 * Singleton helper for publishing metrics with consistent dimensions.
 *
 * Extracts site_code, login_method from URL params and stage from environment.
 * All metrics automatically include these dimensions for filtering by location.
 *
 * Usage:
 *   import { metricsHelper } from './metrics/metricsHelper';
 *
 *   // Initialize once on app load (done in index.tsx)
 *   metricsHelper.initializeFromUrl();
 *
 *   // Publish a counter
 *   metricsHelper.publishCounter(MetricNames.PAGE_LOAD);
 *
 *   // Publish a timer
 *   metricsHelper.publishTimer(MetricNames.PAGE_LOAD_LATENCY, 150);
 *
 *   // Publish with additional dimensions
 *   metricsHelper.publishCounter(MetricNames.LOGIN_BUTTON_CLICK, {
 *     login_method: 'MAASK'
 *   });
 */
class MetricsHelper {
    private dimensions: MetricsDimensions | null = null;
    private isInitialized = false;
    private pageLoadStartTime: number | null = null;

    /**
     * Initialize dimensions from URL parameters and environment.
     * Should be called once on app load.
     */
    initializeFromUrl(): void {
        if (this.isInitialized) {
            return;
        }

        // Capture page load start time for latency calculation
        this.pageLoadStartTime = performance.now();

        const urlParams = new URLSearchParams(window.location.search);
        const siteCode = urlParams.get('site_code');
        const isMaask = urlParams.get('maask') === 'true';

        // Stage is injected at build time via webpack
        const stage: string = 'prod';

        this.dimensions = {
            [DimensionNames.SITE_CODE]: siteCode,
            [DimensionNames.LOGIN_METHOD]: isMaask ? LoginMethodValues.MAASK : LoginMethodValues.ATOZ,
            [DimensionNames.STAGE]: stage,
        };

        this.isInitialized = true;
    }

    /**
     * Get the current dimensions. Returns null if not initialized.
     */
    getDimensions(): MetricsDimensions | null {
        return this.dimensions;
    }

    getSiteCode(): string | null {
        return this.dimensions?.[DimensionNames.SITE_CODE] ?? null;
    }

    getLoginMethod(): LoginMethodValues | null {
        return this.dimensions?.[DimensionNames.LOGIN_METHOD] ?? null;
    }

    getStage(): string {
        return this.dimensions?.[DimensionNames.STAGE] ?? 'unknown';
    }

    /**
     * Calculate page load latency from initialization time
     */
    getPageLoadLatency(): number | null {
        if (this.pageLoadStartTime === null) {
            return null;
        }
        return Math.round(performance.now() - this.pageLoadStartTime);
    }

    /**
     * Build CloudWatch dimensions array from current dimensions.
     * These are actual CloudWatch metric dimensions, not string metrics.
     */
    private buildCloudWatchDimensions(
        additionalDimensions?: Record<string, string>
    ): KatalMetrics.Metric.String[] {
        const allDimensions: Record<string, string | null | undefined> = {
            [DimensionNames.STAGE]: this.dimensions?.[DimensionNames.STAGE],
            [DimensionNames.SITE_CODE]: this.dimensions?.[DimensionNames.SITE_CODE],
            [DimensionNames.LOGIN_METHOD]: this.dimensions?.[DimensionNames.LOGIN_METHOD],
            ...additionalDimensions,
        };

        return Object.entries(allDimensions)
            .filter((entry): entry is [string, string] => entry[1] != null)
            .map(([key, value]) => new KatalMetrics.Metric.String(key, value));
    }

    /**
     * Create a metrics publisher for a specific method/action with CloudWatch dimensions.
     * Uses withCloudWatchDimensions() so dimensions appear in CloudWatch Metrics,
     * not just in CloudWatch Logs.
     */
    private createPublisher(
        methodName: string,
        additionalDimensions?: Record<string, string>
    ): KatalMetrics.Publisher {
        const cloudWatchDimensions = this.buildCloudWatchDimensions(additionalDimensions);
        const childContext = new KatalMetrics.Context({
            cloudWatchDimensions: cloudWatchDimensions,
        });
        return initialMetricsPublisher.newChildActionPublisherForMethod(
            methodName,
            childContext
        );
    }

    /**
     * Publish a counter metric with base dimensions plus any additional dimensions.
     * All dimensions are published as CloudWatch metric dimensions.
     *
     * @param metricName - The metric name from MetricNames enum
     * @param additionalDimensions - Optional additional string dimensions to include
     */
    publishCounter(
        metricName: MetricNames | string,
        additionalDimensions?: Record<string, string>
    ): void {
        const publisher = this.createPublisher(metricName, additionalDimensions);
        publisher.publishCounterMonitor(metricName, 1);
    }

    /**
     * Publish a timer metric with base dimensions plus any additional dimensions.
     * All dimensions are published as CloudWatch metric dimensions.
     *
     * @param metricName - The metric name from MetricNames enum
     * @param durationMs - Duration in milliseconds
     * @param additionalDimensions - Optional additional string dimensions to include
     */
    publishTimer(
        metricName: MetricNames | string,
        durationMs: number,
        additionalDimensions?: Record<string, string>
    ): void {
        const publisher = this.createPublisher(metricName, additionalDimensions);
        publisher.publishTimerMonitor(metricName, durationMs);
    }

    /**
     * Publish an error metric with error details.
     *
     * @param metricName - The metric name from MetricNames enum
     * @param error - The error object
     * @param componentName - Optional component name where the error occurred
     */
    publishError(
        metricName: MetricNames | string,
        error: Error,
        componentName?: string
    ): void {
        const additionalDimensions: Record<string, string> = {
            [DimensionNames.ERROR_MESSAGE]: error.message.substring(0, 200), // Truncate to avoid high cardinality
        };

        if (componentName) {
            additionalDimensions[DimensionNames.ERROR_COMPONENT] = componentName;
        }

        this.publishCounter(metricName, additionalDimensions);
    }

    /**
     * Build CloudWatch dimensions for aggregate metrics (stage only, no site_code).
     * Used for metrics that need to be alarmed on across all sites.
     */
    private buildAggregateCloudWatchDimensions(): KatalMetrics.Metric.String[] {
        const stage = this.dimensions?.[DimensionNames.STAGE];
        if (!stage) {
            return [];
        }
        return [new KatalMetrics.Metric.String(DimensionNames.STAGE, stage)];
    }

    /**
     * Create a metrics publisher for aggregate metrics (stage dimension only).
     * Used for alarms that need to aggregate across all sites.
     */
    private createAggregatePublisher(methodName: string): KatalMetrics.Publisher {
        const cloudWatchDimensions = this.buildAggregateCloudWatchDimensions();
        const childContext = new KatalMetrics.Context({
            cloudWatchDimensions: cloudWatchDimensions,
        });
        return initialMetricsPublisher.newChildActionPublisherForMethod(
            methodName,
            childContext
        );
    }

    /**
     * Publish an aggregate counter metric with only stage dimension.
     * Used for metrics that need to alarm across all sites without knowing
     * individual site_code values. CloudWatch alarms don't support SEARCH
     * expressions, so this provides an alarmable metric.
     *
     * @param metricName - The metric name from MetricNames enum
     */
    publishCounterAggregate(metricName: MetricNames | string): void {
        const publisher = this.createAggregatePublisher(metricName);
        publisher.publishCounterMonitor(metricName, 1);
    }
}

// Export singleton instance
export const metricsHelper = new MetricsHelper();

// Also export the class for testing purposes
export { MetricsHelper };
